import api from "api/api";

const SHORTHAND_GRAMMAR_END_POINT = "/shorthand-grammar";
/** Study log for Add, Store success, and Add/Store failure. */
export const QUICK_ADD_SHORTHAND_LOG_END_POINT = "/quick-add/shorthand-logs";

/** Backend rejects longer values — keep under the limit so logging never 422s. */
export const QUICK_ADD_SHORTHAND_LOG_MAX_LENGTH = 1000;

/** Study logs on production and staging deploys (not local development). */
const shouldLogQuickAddShorthand = () =>
  ["production", "staging"].includes(process.env.REACT_APP_NODE_ENV);

/** Trim and cap length so study-log payloads never trip backend validation. */
export function truncateQuickAddLogField(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  if (trimmed.length <= QUICK_ADD_SHORTHAND_LOG_MAX_LENGTH) return trimmed;
  return trimmed.slice(0, QUICK_ADD_SHORTHAND_LOG_MAX_LENGTH);
}

/** Grammar pack for Quick Add shorthand parsing. */
export const fetchShorthandGrammar = async () => {
  return await api.get(SHORTHAND_GRAMMAR_END_POINT);
};

const QUICK_ADD_DRAFTS_END_POINT = "/option-rows/quick-add/drafts";

/**
 * Staged options live on the server as Pricer option trees hidden from the
 * Pricer list, so grid edits reprice through POST /option-rows/update exactly
 * like the Pricer. Every draft response carries { rows, inputs } in the Pricer
 * row format. Errors resolve (not throw) — see the axios interceptor.
 */
export const fetchQuickAddDrafts = async () => {
  return await api.get(QUICK_ADD_DRAFTS_END_POINT);
};

/** Body: { table_data, shorthands, calculation_date } */
export const stageQuickAddDrafts = async (body) => {
  return await api.post(QUICK_ADD_DRAFTS_END_POINT, {}, body);
};

export const discardQuickAddDrafts = async (summaryIds) => {
  return await api.delete(QUICK_ADD_DRAFTS_END_POINT, {}, {
    summary_ids: summaryIds,
  });
};

export const duplicateQuickAddDraft = async (summaryId) => {
  return await api.post(`${QUICK_ADD_DRAFTS_END_POINT}/${summaryId}/duplicate`);
};

export const submitQuickAddDraftsToPricer = async (summaryIds) => {
  return await api.post(`${QUICK_ADD_DRAFTS_END_POINT}/submit`, {}, {
    summary_ids: summaryIds,
  });
};

export const fetchQuickAddCopyText = async (summaryIds) => {
  return await api.post(`${QUICK_ADD_DRAFTS_END_POINT}/copy-text`, {}, {
    summary_ids: summaryIds,
  });
};

/** After the Team Run popup closes: drops the drafts that were pushed. */
export const finishQuickAddTeamRunPush = async (summaryIds) => {
  return await api.post(`${QUICK_ADD_DRAFTS_END_POINT}/finish-push`, {}, {
    summary_ids: summaryIds,
  });
};

/**
 * Build the study-log JSON. Returns null when the POST should be skipped.
 *
 * 1) Add accepted: { shorthand } — omit row_edited and validation_message.
 * 2) Store success: { shorthand, row_edited } — omit validation_message.
 * 3) Add/Store fail: { shorthand, validation_message, row_edited }.
 *    Skip the whole POST if validation_message is empty after trim.
 */
export function buildQuickAddShorthandLogBody(rawInput, options = {}) {
  const shorthand = truncateQuickAddLogField(rawInput);
  if (!shorthand) return null;

  const wantsFailure = Object.prototype.hasOwnProperty.call(
    options,
    "validationMessage"
  );
  const message = truncateQuickAddLogField(options.validationMessage);
  if (wantsFailure && !message) return null;

  const body = { shorthand };
  if (typeof options.rowEdited === "boolean") {
    body.row_edited = options.rowEdited;
  }
  if (message) body.validation_message = message;
  return body;
}

/**
 * Fire-and-forget study log. Production/staging only. Never throws, never toasts.
 * Caller must not await. Axios treats the URL as silent. 204 expected.
 *
 * @param {string} rawInput
 * @param {{ validationMessage?: string, rowEdited?: boolean }} [options]
 */
export function logQuickAddShorthand(rawInput, options) {
  void logQuickAddShorthandAsync(rawInput, options);
}

async function logQuickAddShorthandAsync(rawInput, options = {}) {
  try {
    if (!shouldLogQuickAddShorthand()) return;
    const body = buildQuickAddShorthandLogBody(rawInput, options);
    if (!body) return;
    await api.post(QUICK_ADD_SHORTHAND_LOG_END_POINT, {}, body);
  } catch {
    // Swallow — study logging must never interrupt staging/store.
  }
}
